// React and 3P
import * as amplitude from '@amplitude/analytics-browser';
import { sessionReplayPlugin } from '@amplitude/plugin-session-replay-browser';
import { PublicClientApplication } from '@azure/msal-browser';
import { MsalProvider } from '@azure/msal-react';
import { QueryClientProvider } from '@tanstack/react-query';
import React from 'react';
import ReactDOM from 'react-dom/client';
import { I18nextProvider } from 'react-i18next';

// Styles
import './styles/rdg-overrides.css'; // Must come AFTER the RDG styles
import './styles/nivo-graph-styles.css';
import 'react-data-grid/lib/styles.css';
import './index.css';
import App from './App';
import { msalConfig } from './authConfig';
import ErrorBoundary from './ErrorBoundary';
import i18n from './i18n';
import reportWebVitals from './reportWebVitals';

// Common and Widgets
import Configs from '@/Configs';
import { AuthProvider } from '@/common/appContext';
import { DataProvider } from '@/common/dataContext';
import { ThemeProvider } from '@/common/ThemeContext';
import { UiShellProvider } from '@/common/uiShellContext';
import { platformQueryClient } from '@/queries/query-client';

const msalInstance = new PublicClientApplication(msalConfig);
await msalInstance.initialize();

// Cache-bust marker so Vite emits a new hashed entry after the 2026-07-24
// truncated immutable JS incident (Chrome kept the bad copy; Safari often did not).
window.__KINCODE_ASSET_BUILD__ = '2026.07.24.gzip-v2';

// Amplitude init - only in production
if (!Configs.devEnvironment) {
  // Standard init
  amplitude.init(Configs.amplitudeAPIKey);
  // Session replay init
  const sessionReplayTracking = sessionReplayPlugin({
    sampleRate: 1, // This configuration samples 50% of all sessions
  });
  amplitude.add(sessionReplayTracking);
}

// --------------------------------------------------------------------------------
// Error handling
// --------------------------------------------------------------------------------
// Ignore ResizeObserver loop error
const resizeObserverLoopErrHandler = () => {
  const resizeObserverErrDiv = document.createElement('div');
  resizeObserverErrDiv.style.display = 'none';
  resizeObserverErrDiv.id = 'resize-observer-error-handler';
  document.body.appendChild(resizeObserverErrDiv);

  const observer = new ResizeObserver(() => {
    const errorHandler = document.getElementById('resize-observer-error-handler');
    if (errorHandler) {
      errorHandler.style.display = 'block';
      setTimeout(() => {
        errorHandler.style.display = 'none';
      }, 1);
    }
  });

  observer.observe(document.body);
};

resizeObserverLoopErrHandler();

// --------------------------------------------------------------------------------
// Render
// --------------------------------------------------------------------------------
const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(
  <React.StrictMode>
    <MsalProvider instance={msalInstance}>
      <QueryClientProvider client={platformQueryClient}>
        <I18nextProvider i18n={i18n}>
          <ThemeProvider>
            <AuthProvider>
              <UiShellProvider>
                <DataProvider>
                  <ErrorBoundary>
                    <App />
                  </ErrorBoundary>
                </DataProvider>
              </UiShellProvider>
            </AuthProvider>
          </ThemeProvider>
        </I18nextProvider>
      </QueryClientProvider>
    </MsalProvider>
  </React.StrictMode>
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
